import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Box, CircularProgress, Popper, Typography, Tooltip } from '@mui/material';
import { FaExpand, FaCompress, FaArrowsRotate } from "react-icons/fa6";
import { FaCamera } from "react-icons/fa";
import { BsCaretLeftFill, BsCaretRightFill, BsXCircle } from "react-icons/bs";
import NaviBox from './NaviBox';

const SCALE_BY = 1.08;
const MAX_SCALE = 8;
const MIN_SCALE = 0.2;
const PANSTEP = 50;

const FixedBox = {
    position: 'absolute',
    top: 0, bottom: 0,
    left: 0, right: 0,
    margin: 'auto',
    zIndex: 1999,
    userSelect: "none",
};

const FlexBox = {
    display: 'flex',
    gap: 2, alignItems: 'center', justifyContent: 'center'
};

const resolveMediaUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    const apiBase = (process.env.REACT_APP_URL || "http://127.0.0.1:8000/api/").replace(/\/api\/?$/, "");
    if (url.includes("localhost:8000") || url.includes("127.0.0.1:8000") || url.includes("localhost:3000")) {
        url = url.replace(/^http:\/\/(localhost|127\.0\.0\.1):(8000|3000)/, apiBase);
    }
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:") || url.startsWith("blob:")) {
        return url;
    }
    if (url.startsWith("/")) {
        return `${apiBase}${url}`;
    }
    return `${apiBase}/${url}`;
};

const ImageViewer = forwardRef(({
    src,
    showPrev = true,
    showNext = true,
    icons = {},
    prevPath = "",
    nextPath = "",
    onChange = (data) => { },
    label = "",
    camera = null,
    showTimeline = false,
    timelineData = {},
    rootPath = "",
    currentDate = "",
    onCloseTimeline = () => { },
    onSelectTimelineDate = (date) => { },
}, ref) => {
    const containerRef = useRef(null);
    const mediaRef = useRef(null);
    const timelineContainerRef = useRef(null);
    const lastTouchDistance = useRef(null);

    const timelineDates = React.useMemo(() => {
        if (!timelineData || typeof timelineData !== 'object') return [];
        return Object.keys(timelineData).sort((a, b) => a.localeCompare(b));
    }, [timelineData]);

    const scrollTimeline = (offset) => {
        if (timelineContainerRef.current) {
            timelineContainerRef.current.scrollBy({
                left: offset,
                behavior: 'smooth',
            });
        }
    };

    const formatDateLabel = (dateStr) => {
        try {
            if (!dateStr) return "";
            const parts = dateStr.split("-");
            if (parts.length === 3) {
                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const year = parts[0];
                const month = months[parseInt(parts[1], 10) - 1] || parts[1];
                const day = parseInt(parts[2], 10);
                return `${day} ${month} ${year}`;
            }
        } catch (e) {}
        return dateStr;
    };

    const getTimelineThumbUrl = (date, entry) => {
        if (!entry) {
            if (date === currentDate && src) return src;
            return "";
        }
        let thumbPath = typeof entry === "object" ? (entry.thumbnail || entry.url || entry.path || "") : entry;
        if (!thumbPath) {
            if (date === currentDate && src) return src;
            return "";
        }
        if (thumbPath.startsWith("http://") || thumbPath.startsWith("https://") || thumbPath.startsWith("data:")) {
            return resolveMediaUrl(thumbPath);
        }
        if (rootPath) {
            return resolveMediaUrl(`${rootPath}/${date}/${thumbPath}`);
        }
        return resolveMediaUrl(thumbPath);
    };

    useEffect(() => {
        if (showTimeline && timelineContainerRef.current && currentDate) {
            const timer = setTimeout(() => {
                const container = timelineContainerRef.current;
                if (!container) return;
                const index = timelineDates.indexOf(currentDate);
                if (index !== -1) {
                    const itemWidth = 155;
                    const scrollPos = Math.max(0, index * itemWidth - container.clientWidth / 2 + itemWidth / 2);
                    container.scrollTo({ left: scrollPos, behavior: 'smooth' });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [showTimeline, currentDate, timelineDates]);

    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [last, setLast] = useState({ x: 0, y: 0 });

    const [loader, setLoader] = useState(true);
    const [popup, setPopup] = useState({});
    const [initData, setInitData] = useState({});
    const [isFullscreen, setIsFullscreen] = useState(false);

    const resolvedSrc = resolveMediaUrl(src);

    useImperativeHandle(ref, () => ({
        getSnapshot: () => {
            const el = mediaRef.current;
            if (!el || !el.complete) return null;
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const width = el.naturalWidth;
            const height = el.naturalHeight;
            if (!width || !height) return null;
            canvas.width = width;
            canvas.height = height;
            try {
                ctx.drawImage(el, 0, 0, width, height);
                return canvas.toDataURL("image/png");
            } catch (e) {
                return null;
            }
        }
    }));

    useEffect(() => {
        const handleChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener("fullscreenchange", handleChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleChange);
        };
    }, []);

    // Workaround for passive event listener warning on wheel events
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const preventDefaultWheel = (e) => e.preventDefault();
        container.addEventListener('wheel', preventDefaultWheel, { passive: false });
        return () => {
            container.removeEventListener('wheel', preventDefaultWheel);
        };
    }, []);

    // ---------------- FIT TO SCREEN ----------------
    useEffect(() => {
        const media = mediaRef.current;
        const container = containerRef.current;
        if (!media || !container) return;

        setLoader(true);

        const fit = () => {
            const cw = container.clientWidth;
            const ch = container.clientHeight;
            const mw = media.naturalWidth || 1920;
            const mh = media.naturalHeight || 1080;

            const fitScale = Math.max(cw / mw, ch / mh);

            setScale(fitScale);
            setPosition({ x: 0, y: 0 });
            setLoader(false);
            setInitData({ fitScale, x: 0, y: 0, mw, mh });
        };

        if (media.complete && media.naturalWidth > 0) {
            fit();
        } else {
            media.onload = fit;
            media.onerror = () => setLoader(false);
        }
    }, [resolvedSrc]);

    // ---------------- CLAMP ------------------------
    const clamp = (x, y, s) => {
        const container = containerRef.current;
        const media = mediaRef.current;
        if (!container || !media) return { x, y };

        const cw = container.clientWidth;
        const ch = container.clientHeight;
        const mw = media.naturalWidth || 1920;
        const mh = media.naturalHeight || 1080;

        const sw = mw * s;
        const sh = mh * s;

        const maxX = Math.max(0, (sw - cw) / 2);
        const maxY = Math.max(0, (sh - ch) / 2);

        return {
            x: Math.min(maxX, Math.max(-maxX, x)),
            y: Math.min(maxY, Math.max(-maxY, y)),
        };
    };

    const clampScale = (s) => {
        const container = containerRef.current;
        const media = mediaRef.current;
        if (!container || !media) return s;

        const cw = container.clientWidth;
        const ch = container.clientHeight;
        const mw = media.naturalWidth || 1920;
        const mh = media.naturalHeight || 1080;

        // Minimum: fit to container dimensions (cannot zoom out smaller than fit, no empty black space)
        const minScale = Math.max(cw / mw, ch / mh);
        // Maximum: allow up to 8x zoom
        const maxScale = Math.max(minScale * 6, MAX_SCALE);

        return Math.max(minScale, Math.min(maxScale, s));
    };

    // ---------------- ZOOM ----------------
    const handleWheel = (e) => {
        const dir = e.deltaY > 0 ? -1 : 1;
        applyZoom(dir, e.clientX, e.clientY);
    };

    const handleZoom = (type) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const dir = type === "in" ? 1 : -1;
        applyZoom(dir, centerX, centerY);
    };

    const applyZoom = (dir, clientX, clientY) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();

        const mx = clientX - rect.left - rect.width / 2;
        const my = clientY - rect.top - rect.height / 2;

        let newScale = dir > 0 ? scale * SCALE_BY : scale / SCALE_BY;
        newScale = clampScale(newScale);

        const ratio = newScale / scale;
        const newX = position.x - mx * (ratio - 1);
        const newY = position.y - my * (ratio - 1);

        const clamped = clamp(newX, newY, newScale);

        setScale(newScale);
        setPosition(clamped);
    };

    const applyMove = (dx, dy) => {
        setPosition((prev) => clamp(prev.x + dx, prev.y + dy, scale));
    };

    // ---------------- DRAG PAN & TILT ----------------
    const onDown = (e) => {
        if (e.button !== 0) return;
        setDragging(true);
        setLast({ x: e.clientX, y: e.clientY });
    };

    const onMove = (e) => {
        if (!dragging) return;
        const dx = e.clientX - last.x;
        const dy = e.clientY - last.y;
        applyMove(dx, dy);
        setLast({ x: e.clientX, y: e.clientY });
    };

    const onUp = () => setDragging(false);

    // ---------------- PINCH ZOOM (Mobile/Touch) ----------------
    const handleTouchMove = (e) => {
        if (e.touches.length !== 2) return;
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

        if (!lastTouchDistance.current) {
            lastTouchDistance.current = dist;
            return;
        }

        let newScale = scale * (dist / lastTouchDistance.current);
        newScale = clampScale(newScale);

        const clamped = clamp(position.x, position.y, newScale);
        setScale(newScale);
        setPosition(clamped);
        lastTouchDistance.current = dist;
    };

    const handleTouchEnd = () => {
        lastTouchDistance.current = null;
    };

    const handlePanZoom = (direction) => {
        if (direction === "reset") {
            setPosition({ x: initData?.x || 0, y: initData?.y || 0 });
            setScale(initData?.fitScale || 1);
            return;
        }
        if (direction === "zoom-in" || direction === "zoom-out") {
            return handleZoom(direction === "zoom-in" ? "in" : "out");
        }
        let dx = 0;
        let dy = 0;
        switch (direction) {
            case "up":
                dy = -PANSTEP;
                break;
            case "down":
                dy = PANSTEP;
                break;
            case "left":
                dx = -PANSTEP;
                break;
            case "right":
                dx = PANSTEP;
                break;
            default:
                break;
        }
        applyMove(dx, dy);
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen?.();
        } else {
            document.exitFullscreen?.();
        }
    };

    const takeSnapshot = (ref, filename = "snapshot.png") => {
        const el = ref.current;
        if (!el || !el.complete) return;
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const width = el.naturalWidth;
        const height = el.naturalHeight;
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(el, 0, 0, width, height);

        const link = document.createElement("a");
        link.download = filename;
        link.href = canvas.toDataURL("image/png");
        link.click();
    };

    return (
        <>
            <div
                className="icon-filled-black"
                ref={containerRef}
                onWheel={handleWheel}
                onMouseDown={onDown}
                onMouseMove={onMove}
                onMouseUp={onUp}
                onMouseLeave={onUp}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{
                    width: "100%",
                    height: "100%",
                    background: "#111",
                    overflow: "hidden",
                    position: "relative",
                    cursor: dragging ? "grabbing" : "grab",
                    touchAction: "none",
                    userSelect: 'none'
                }}
                onDoubleClick={() => handlePanZoom("reset")}
            >
                {/* Timeline Strip */}
                {showTimeline && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 2200,
                            background: localStorage?.selectedTheme === 'dark' ? 'rgba(24, 24, 27, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            borderBottom: '1px solid',
                            borderColor: localStorage?.selectedTheme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                            py: 1,
                            px: 1.5,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                            {/* Scroll Left Button */}
                            <Typography
                                onClick={() => scrollTimeline(-280)}
                                sx={{
                                    color: localStorage?.selectedTheme === 'dark' ? '#fff' : '#111',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 0.5,
                                    flexShrink: 0,
                                    userSelect: 'none',
                                    opacity: 0.8,
                                    '&:hover': { opacity: 1, transform: 'scale(1.15)' },
                                    transition: 'all 0.15s'
                                }}
                            >
                                <BsCaretLeftFill fontSize={36} />
                            </Typography>

                            {/* Thumbnails Row */}
                            <Box
                                ref={timelineContainerRef}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    overflowX: 'auto',
                                    gap: 1.5,
                                    py: 0.8,
                                    px: 1,
                                    mx: 0.5,
                                    width: '100%',
                                    alignItems: 'center',
                                    scrollBehavior: 'smooth',
                                    '&::-webkit-scrollbar': { height: 6 },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: localStorage?.selectedTheme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                                        borderRadius: 3
                                    }
                                }}
                            >
                                {timelineDates.map((date) => {
                                    const thumbEntry = timelineData[date];
                                    const thumbUrl = getTimelineThumbUrl(date, thumbEntry);
                                    const isSelected = currentDate === date;
                                    return (
                                        <Box
                                            key={date}
                                            onClick={() => onSelectTimelineDate(date)}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                                p: 0.5,
                                                borderRadius: '8px',
                                                border: isSelected ? '2px solid #ffcc33' : '1px solid rgba(128,128,128,0.25)',
                                                background: isSelected ? 'rgba(255, 204, 51, 0.18)' : 'transparent',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    borderColor: '#ffcc33',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
                                                }
                                            }}
                                        >
                                            <img
                                                src={thumbUrl || undefined}
                                                alt={date}
                                                style={{
                                                    width: 140,
                                                    height: 64,
                                                    objectFit: 'cover',
                                                    borderRadius: 4,
                                                    backgroundColor: '#111'
                                                }}
                                                onError={(e) => {
                                                    const rawPath = typeof thumbEntry === 'object' ? (thumbEntry?.thumbnail || '') : thumbEntry;
                                                    if (rawPath && rawPath.includes('thumbnail/')) {
                                                        e.target.src = resolveMediaUrl(`${rootPath}/${date}/${rawPath.replace('thumbnail/', '')}`);
                                                    } else if (date === currentDate && src) {
                                                        e.target.src = src;
                                                    } else {
                                                        e.target.style.display = 'none';
                                                    }
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    fontWeight: isSelected ? 700 : 500,
                                                    color: isSelected ? '#ffcc33' : (localStorage?.selectedTheme === 'dark' ? '#eee' : '#111'),
                                                    mt: 0.5,
                                                    textAlign: 'center',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {formatDateLabel(date)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* Scroll Right Button */}
                            <Typography
                                onClick={() => scrollTimeline(280)}
                                sx={{
                                    color: localStorage?.selectedTheme === 'dark' ? '#fff' : '#111',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 0.5,
                                    flexShrink: 0,
                                    userSelect: 'none',
                                    opacity: 0.8,
                                    '&:hover': { opacity: 1, transform: 'scale(1.15)' },
                                    transition: 'all 0.15s'
                                }}
                            >
                                <BsCaretRightFill fontSize={36} />
                            </Typography>

                            {/* Close (X) button at top right */}
                            <Box
                                onClick={onCloseTimeline}
                                sx={{
                                    position: 'absolute',
                                    top: -4,
                                    right: 2,
                                    color: '#ef4444',
                                    cursor: 'pointer',
                                    p: 0.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 2210,
                                    transition: 'transform 0.15s',
                                    '&:hover': {
                                        transform: 'scale(1.2)',
                                        color: '#dc2626'
                                    }
                                }}
                                title="Close Timeline"
                            >
                                <BsXCircle size={18} />
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Top Control Bar */}
                <Box sx={{
                    ...FixedBox,
                    top: showTimeline ? 116 : 16,
                    bottom: 'auto',
                    left: 'auto',
                    right: 16,
                    px: 1.5,
                    py: 1,
                    width: 'auto',
                    height: 'auto',
                    zIndex: 2001,
                }}>
                    <Box sx={{ ...FlexBox, gap: 1, color: 'white' }}>
                        {/* 1.0x Reset Button when zoomed */}
                        {Math.abs(scale - (initData?.fitScale || 1)) > 0.05 && (
                            <Tooltip title="Reset to original fit" arrow>
                                <Box
                                    onClick={() => handlePanZoom("reset")}
                                    sx={{
                                        ...FlexBox,
                                        cursor: 'pointer',
                                        background: '#ffcc33',
                                        color: '#000',
                                        borderRadius: '6px',
                                        px: 1,
                                        height: 26,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        gap: 0.5,
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                                    }}
                                >
                                    <FaArrowsRotate size={11} />
                                    <span>1.0x</span>
                                </Box>
                            </Tooltip>
                        )}

                        <Box
                            sx={{
                                ...FlexBox,
                                px: 0.8,
                                py: 0.4,
                                background: 'rgba(0,0,0,0.7)',
                                borderRadius: '6px',
                                border: '1px solid rgba(255,255,255,0.2)',
                                height: 26,
                                boxSizing: 'border-box',
                                cursor: 'pointer'
                            }}
                            onClick={() => takeSnapshot(mediaRef, `${label || 'timelapse'}_${Date.now()}.png`)}
                        >
                            <FaCamera className="pointer" color="white" size={15} />
                        </Box>

                        <Box
                            className="pointer"
                            sx={{
                                ...FlexBox,
                                borderRadius: '6px',
                                border: '1px solid rgba(255,255,255,0.3)',
                                minWidth: 26,
                                fontSize: 13,
                                height: 26,
                                background: '#000',
                                px: 0.5,
                                boxSizing: 'border-box'
                            }}
                        >
                            {isFullscreen ? (
                                <FaCompress style={{ marginBottom: 2 }} onClick={() => toggleFullscreen()} />
                            ) : (
                                <FaExpand onClick={() => toggleFullscreen()} />
                            )}
                        </Box>
                    </Box>
                </Box>

                {loader && (
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 2500,
                    }}>
                        <CircularProgress size={38} sx={{ color: "#ffcc33" }} />
                        <Typography sx={{ color: "#ffcc33", fontSize: '12px', fontWeight: 600, mt: 1 }}>
                            Loading...
                        </Typography>
                    </Box>
                )}

                {/* Prev / Next Navigation Arrows */}
                <Box>
                    {showPrev && (
                        <Box
                            onMouseEnter={(e) => setPopup({ target: e.target, prev: true })}
                            onMouseLeave={() => setPopup({})}
                            onClick={() => { setLoader(true); onChange({ type: "prev" }); }}
                            sx={{ ...FixedBox, ...FlexBox, right: 'auto', left: 20, width: 40, height: 64, cursor: 'pointer' }}
                        >
                            {icons?.backward}
                        </Box>
                    )}
                    {showNext && (
                        <Box
                            onMouseEnter={(e) => setPopup({ target: e.target, next: true })}
                            onMouseLeave={() => setPopup({})}
                            onClick={() => { setLoader(true); onChange({ type: "next" }); }}
                            sx={{ ...FixedBox, ...FlexBox, right: 20, left: 'auto', width: 40, height: 64, cursor: 'pointer' }}
                        >
                            {icons?.forward}
                        </Box>
                    )}
                </Box>

                {/* Main Timelapse Image */}
                <img
                    ref={mediaRef}
                    src={resolvedSrc || undefined}
                    alt=""
                    draggable={false}
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
                        transformOrigin: "center",
                        transition: dragging ? "none" : "transform 0.25s cubic-bezier(0.2, 0, 0, 1)",
                        pointerEvents: "none",
                        userSelect: "none"
                    }}
                />

                {/* NaviBox Controls */}
                <Box sx={{
                    ...FixedBox,
                    top: 'auto',
                    bottom: 24,
                    width: 140,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    overflow: 'visible'
                }}>
                    <NaviBox icons={icons} onChange={handlePanZoom} />
                </Box>
            </div>

            {/* Thumbnail Hover Popup */}
            <Popper
                open={Boolean(popup?.target)}
                anchorEl={popup?.target}
                placement={popup?.next ? "left-start" : "right-start"}
                modifiers={[{ name: 'offset', options: { offset: [24, 0] } }]}
                sx={{ zIndex: 9999, borderRadius: 2, p: 0, display: { xs: 'none', sm: 'block' } }}
            >
                <Box sx={{ height: 120, borderRadius: 2, border: '1px solid white', overflow: 'hidden', background: '#000' }}>
                    {(showNext && popup?.next) && <img src={resolveMediaUrl(nextPath) || undefined} height={120} alt="" />}
                    {(showPrev && popup?.prev) && <img src={resolveMediaUrl(prevPath) || undefined} height={120} alt="" />}
                </Box>
            </Popper>
        </>
    );
});

export default ImageViewer;
